import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@store';
import {
  formatDate,
  IconLoader,
  PermissionEnum,
  SchemaFilterType,
  Table,
  useGetList,
  useQueryState,
} from '@ui/src';
import { useLazyGetTransactionsImportHistoryQuery } from '../transactions/store/services';
import { setStoreTakeCount } from './store/slices/list';
import { columns, columnsLabels, schema } from './variables/history';
import { PaymentOrder, StatusTitle } from '../cash-collection/types';
import { TransactionListTypes } from './types';

import cn from './styles.module.css';

const formatImportHistoryList = ({
  list,
}: {
  list: PaymentOrder[];
}): PaymentOrder[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, any> = {
      orderCreatedAt: (
        <div>{formatDate(result?.orderCreatedAt || '', true)}</div>
      ),
      // @ts-ignore
      status: <div>{StatusTitle[item.status as StatusTitle]}</div>,
      pspPartnerId: <div>{result?.pspPartner?.companyName || '-'}</div>,
      pspPartnerAccountId: (
        <div>{result?.pspPartnerAccount?.fullNumber || '-'}</div>
      ),
      uploadedByEmployeeId: (
        <div>
          {result?.uploadedByEmployeeId
            ? `${result?.uploadedByEmployee?.profile?.firstName} ${result?.uploadedByEmployee?.profile?.lastName}`
            : '-'}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const TransactionList = ({
  isSuccess,
}: TransactionListTypes): React.JSX.Element => {
  const [getTransactionsImportHistory, { isLoading, isFetching }] =
    useLazyGetTransactionsImportHistoryQuery();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [takeCount, setTakeCount] = useState(0);

  const dispatch = useAppDispatch();

  const {
    import: {
      list: { importHistory, pagination, errorsList },
    },
    transactionsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const { getListAction, setPage, onSort, errorList } =
    useGetList<PaymentOrder>({
      getList: getTransactionsImportHistory,
      onFilterChange,
      onSortChange,
      schema,
    });

  const formattedData = useMemo(() => {
    const data = formatImportHistoryList({
      list: importHistory as PaymentOrder[],
    });

    return data;
  }, [importHistory]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_INCOMING_LIST) &&
      takeCount !== 0
    ) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
          type: {
            value: ['BATCH_IMPORT_INCOMING_TRANSACTIONS'],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [activePermissions, takeCount]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 50;

        setTakeCount(Math.ceil(take + 3));
        dispatch(setStoreTakeCount(Math.ceil(take + 3).toString()));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (isSuccess) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
          type: {
            value: ['BATCH_IMPORT_INCOMING_TRANSACTIONS'],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [isSuccess]);

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.importLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && !isFetching && importHistory?.length) {
    content = (
      <Table
        columns={columns}
        data={formattedData || []}
        onSort={(name) => {
          onSort(name);
        }}
        filters={filters}
        sort={sort}
        columnsLabels={columnsLabels}
        onFilterChange={() => {}}
        isLoading={isLoading}
        take={takeCount?.toString()}
        setPage={(data) => {
          setPage({
            ...data,
            filters: {
              ...filters,
              take: {
                value: [takeCount?.toString()],
                type: SchemaFilterType.STRING,
              },
              type: {
                value: ['BATCH_IMPORT_INCOMING_TRANSACTIONS'],
                type: SchemaFilterType.STRING,
              },
            },
          });
        }}
        pagination={pagination}
        error={errorsList || errorList}
        paginationDisabled={isFetching || isLoading}
        isFetching={isFetching}
        clickable
      />
    );
  } else if (!importHistory?.length && !isLoading && !isFetching) {
    content = (
      <div className={cn.emptyList}>Transactions have not been added yet</div>
    );
  }

  return (
    <div className={cn.uploadHistoryWrapper}>
      <div className={cn.uploadHistoryTitle}>Upload History</div>
      <div className={cn.uploadHistoryTableWrapper} ref={scrollRef}>
        {content}
      </div>
    </div>
  );
};

export default TransactionList;
